/**
 * Impact Simulation - Scenarios List Page
 * Browse, create, and manage simulation scenarios
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FlaskConical,
  Plus,
  Play,
  CheckCircle2,
  Clock,
  TrendingDown,
  DollarSign,
  Target,
  Trash2,
  Loader2,
  ArrowRight,
  BarChart3,
  Shield
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useToast } from '../../hooks/use-toast';
import { apiFetch } from '../../services/api_service';


const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'text-[#6B6B6B]', bg: 'bg-[#1F1F1F]' },
  simulated: { label: 'Simulated', color: 'text-blue-400', bg: 'bg-blue-500/20' },
  committed: { label: 'Committed', color: 'text-emerald-400', bg: 'bg-emerald-500/20' },
};

function formatCost(val) {
  if (!val) return '$0';
  if (val >= 1e9) return `$${(val / 1e9).toFixed(1)}B`;
  if (val >= 1e6) return `$${(val / 1e6).toFixed(0)}M`;
  if (val >= 1e3) return `$${(val / 1e3).toFixed(0)}K`;
  return `$${val}`;
}

function formatEmissions(val) {
  if (!val && val !== 0) return '-';
  if (val >= 1e6) return `${(val / 1e6).toFixed(1)}M tCO2e`;
  if (val >= 1e3) return `${(val / 1e3).toFixed(0)}K tCO2e`;
  return `${val} tCO2e`;
}

export default function SimulationListPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newYear, setNewYear] = useState(2030);

  const fetchScenarios = async () => {
    try {
      const data = await apiFetch(`/v1/simulations`);
      if (data && !data._error) {
        setScenarios(data.scenarios || []);
      }
    } catch (e) {
      console.error('Failed to fetch scenarios:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchScenarios(); }, []);

  const createScenario = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const data = await apiFetch(`/v1/simulations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          scenarioName: newName,
          target: { type: 'net_zero', targetYear: newYear },
        }),
      });
      if (data.success) {
        navigate(`/simulations/${data.scenario.scenarioId}`);
      }
    } catch {
      toast({ title: 'Error', description: 'Failed to create scenario', variant: 'destructive' });
    } finally {
      setCreating(false);
    }
  };

  const deleteScenario = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this scenario?')) return;
    setScenarios(prev => prev.filter(s => s.scenarioId !== id));
    try {
      await apiFetch(`/v1/simulations/${id}`, { method: 'DELETE' });
      toast({ title: 'Deleted', description: 'Scenario removed' });
    } catch {
      fetchScenarios();
    }
  };

  return (
    <div className="space-y-6" data-testid="simulation-list-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <FlaskConical className="w-6 h-6 text-violet-400" />
            Impact Simulation
          </h1>
          <p className="text-sm text-[#6B6B6B] mt-1">
            Model "what-if" scenarios for ESG decisions before committing
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => navigate('/simulations/templates')}
            className="border-[#2F2F2F] text-[#A0A0A0] hover:text-white hover:border-violet-500/30"
            data-testid="browse-templates-btn"
          >
            <BarChart3 className="w-4 h-4 mr-2" />
            Browse Templates
          </Button>
          <Button
            onClick={() => setShowCreateForm(true)}
            className="bg-violet-500/20 text-violet-400 border border-violet-500/30 hover:bg-violet-500/30"
            data-testid="create-scenario-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Scenario
          </Button>
        </div>
      </div>

      {/* Create Form */}
      {showCreateForm && (
        <div className="p-4 bg-[#111111] border border-violet-500/30 rounded-xl space-y-3" data-testid="create-form">
          <h3 className="text-sm font-medium text-white">Create New Scenario</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input
              type="text"
              placeholder="Scenario name..."
              value={newName}
              onChange={e => setNewName(e.target.value)}
              className="bg-[#0D0D0D] border border-[#2F2F2F] rounded-lg px-3 py-2 text-sm text-white placeholder-[#6B6B6B] focus:border-violet-500/50 focus:outline-none md:col-span-2"
              data-testid="scenario-name-input"
              autoFocus
            />
            <div className="flex gap-2">
              <select
                value={newYear}
                onChange={e => setNewYear(+e.target.value)}
                className="flex-1 bg-[#0D0D0D] border border-[#2F2F2F] rounded-lg px-3 py-2 text-sm text-white"
                data-testid="target-year-select"
              >
                {[2026,2027,2028,2029,2030,2035,2040,2050].map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
              <Button onClick={createScenario} disabled={creating || !newName.trim()} className="bg-violet-600 hover:bg-violet-700" data-testid="confirm-create-btn">
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}
              </Button>
              <Button variant="ghost" onClick={() => setShowCreateForm(false)} className="text-[#6B6B6B]">Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {/* Scenarios List */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 text-violet-400 animate-spin" />
        </div>
      ) : scenarios.length === 0 ? (
        <div className="text-center py-16 text-[#6B6B6B]">
          <FlaskConical className="w-12 h-12 mx-auto mb-3 opacity-20" />
          <p className="text-lg mb-1">No scenarios yet</p>
          <p className="text-sm">Create your first "what-if" scenario to model ESG strategies</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="scenarios-grid">
          {scenarios.map(scenario => (
            <ScenarioCard
              key={scenario.scenarioId}
              scenario={scenario}
              onClick={() => navigate(`/simulations/${scenario.scenarioId}`)}
              onDelete={(e) => deleteScenario(scenario.scenarioId, e)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ScenarioCard({ scenario, onClick, onDelete }) {
  const status = STATUS_CONFIG[scenario.status] || STATUS_CONFIG.draft;
  const fr = scenario.simulationRun?.finalResults;
  const hasResults = !!fr;

  return (
    <div
      className="p-4 bg-[#111111] border border-[#1F1F1F] rounded-xl hover:border-violet-500/30 cursor-pointer transition-all group"
      onClick={onClick}
      data-testid={`scenario-card-${scenario.scenarioId}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="text-sm font-medium text-white group-hover:text-violet-400 transition-colors">
            {scenario.scenarioName}
          </h3>
          <p className="text-xs text-[#6B6B6B] mt-0.5">
            Target: {scenario.target?.type?.replace(/_/g, ' ')} by {scenario.target?.targetYear}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={`${status.bg} ${status.color} border-0`}>{status.label}</Badge>
          <button onClick={onDelete} className="opacity-0 group-hover:opacity-100 text-[#6B6B6B] hover:text-red-400 transition-all" data-testid={`delete-${scenario.scenarioId}`}>
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-3 text-xs text-[#6B6B6B]">
        <span>{scenario.actions?.length || 0} actions</span>
        <span className="text-[#2F2F2F]">|</span>
        <span>{new Date(scenario.createdAt).toLocaleDateString()}</span>
      </div>

      {hasResults ? (
        <div className="grid grid-cols-4 gap-2">
          <MiniStat
            icon={TrendingDown}
            value={`${fr.emissionsReduction.percentageReduction}%`}
            label="Reduction"
            color={fr.emissionsReduction.achievesNetZero ? 'text-emerald-400' : 'text-amber-400'}
          />
          <MiniStat icon={DollarSign} value={formatCost(fr.financialSummary.totalCost)} label="Total Cost" color="text-blue-400" />
          <MiniStat icon={Shield} value={`${fr.complianceScores.csrdScore}`} label="CSRD" color="text-purple-400" />
          <MiniStat icon={Target} value={fr.riskAssessment.overallRisk} label="Risk" color={fr.riskAssessment.overallRisk === 'low' ? 'text-emerald-400' : fr.riskAssessment.overallRisk === 'medium' ? 'text-amber-400' : 'text-red-400'} />
        </div>
      ) : (
        <div className="flex items-center gap-2 text-xs text-[#6B6B6B]">
          <Play className="w-3 h-3" />
          <span>Add actions and run simulation to see results</span>
        </div>
      )}
    </div>
  );
}

function MiniStat({ icon: Icon, value, label, color }) {
  return (
    <div className="text-center">
      <Icon className={`w-3 h-3 mx-auto mb-0.5 ${color}`} />
      <p className={`text-xs font-bold ${color}`}>{value}</p>
      <p className="text-[10px] text-[#6B6B6B]">{label}</p>
    </div>
  );
}
